import React, { useState, useEffect } from 'react';

import { pageAccessAPI } from '../../utils/api';
import { useAuth } from '../../hooks/useAuth';

const PERM_LEVELS      = ['SystemAdmin','Admin','Executive','Manager','Specialist','Developer'];
// Levels whose access is unconditional and must not be toggled off.
const LOCKED_LEVELS    = ['SystemAdmin','Admin'];
const PERM_LEVEL_COLOR = { Admin:'#2E539F', Executive:'#8b5cf6', Manager:'#0891b2', Specialist:'#10b981', Developer:'#6d28d9' };

// Role → level reference for the info panel
const LEVEL_ROLES = {
  Admin:      ['Administrator','Admin'],
  Executive:  ['CEO','CPO','Chief Operating Officer'],
  Manager:    ['Manager','Head of Product Delivery'],
  Specialist: ['Impl. & Training Specialist','Senior Impl. Specialist','Communication Specialist','Representative','Senior Expert Risk & Analytics'],
  Developer:  ['Developer','Senior Developer'],
};
const PERM_LEVEL_COLOR_EXT = { Admin:'#2E539F', Executive:'#8b5cf6', Manager:'#0891b2', Specialist:'#10b981', Developer:'#8b5cf6' };
const EXEC_NOTE = 'Executive level (CEO, CPO, COO) inherits Manager-level page access.';

function LockIc() {
  return <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>;
}

export default function AdminPageAccessPage() {
  const { user }                        = useAuth();
  const [pages,         setPages]       = useState([]);
  const [loading,       setLoading]     = useState(true);
  const [error,         setError]       = useState('');
  const [saving,        setSaving]      = useState({});
  const [toast,         setToast]       = useState(null);
  const [moduleFilter,  setModuleFilter] = useState('All');
  const [levelFilter,   setLevelFilter]  = useState('Manager');
  const [pageFilter,    setPageFilter]   = useState('');

  const show = (msg, err=false) => {
    setToast({ msg, err });
    setTimeout(() => setToast(null), 2800);
  };

  useEffect(() => {
    pageAccessAPI.getAll()
      .then(r => setPages(r.data || []))
      .catch(e => setError(e.response?.data?.message || 'Failed to load page access data'))
      .finally(() => setLoading(false));
  }, []);

  const togglePageAccess = async (pageId, roleName, currentlyOn, type='access') => {
    const key = pageId + roleName + type;
    setSaving(s => ({ ...s, [key]: true }));
    try {
      await pageAccessAPI.toggle(pageId, roleName, type, !currentlyOn);
      setPages(prev => prev.map(pg => {
        if (pg.id !== pageId) return pg;
        const cur = pg.access?.[roleName];
        const obj = typeof cur === 'object' && cur !== null
          ? { ...cur }
          : { on: typeof cur === 'boolean' ? cur : false, read:true, write:false, update:false };
        if (type === 'access') obj.on     = !currentlyOn;
        if (type === 'read')   obj.read   = !currentlyOn;
        if (type === 'write')  obj.write  = !currentlyOn;
        if (type === 'update') obj.update = !currentlyOn;
        return { ...pg, access: { ...pg.access, [roleName]: obj } };
      }));
      const label = type === 'access'
        ? (!currentlyOn ? 'Access granted' : 'Access revoked')
        : `${type.charAt(0).toUpperCase()+type.slice(1)} ${!currentlyOn?'enabled':'disabled'}`;
      show(label + ' for ' + roleName);
    } catch(e) {
      show(e.response?.data?.message || 'Could not update access', true);
    } finally {
      setSaving(s => { const n={...s}; delete n[key]; return n; });
    }
  };

  const pagesByGroup = pages.reduce((acc, p) => {
    const g = p.module_group || 'Other';
    if (!acc[g]) acc[g] = [];
    acc[g].push(p);
    return acc;
  }, {});

  if (loading) return (
    <div className="flex justify-center py-20">
      <div className="w-7 h-7 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-[300] px-4 py-2.5 rounded-xl text-sm font-medium text-white shadow-xl"
          style={{ background: toast.err ? '#ef4444' : '#10b981' }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="page-title">Page &amp; Module Access</h1>
        <p className="text-sm mt-0.5" style={{ color:'var(--text-secondary)' }}>
          Control which pages and modules each permission level can access — and what they can do on each page.
        </p>
      </div>

      {error && (
        <div className="p-4 rounded-xl text-sm" style={{ background:'rgba(239,68,68,.08)', color:'#ef4444', border:'1px solid rgba(239,68,68,.2)' }}>⚠ {error}</div>
      )}

      {/* Info banner */}
      <div className="card-alt text-sm space-y-1">
        <p className="font-semibold" style={{ color:'var(--text)' }}>What this controls</p>
        <p style={{ color:'var(--text-secondary)' }}>
          <strong>Access</strong> = page visible in sidebar &nbsp;·&nbsp;
          <strong>Read</strong> = can view data &nbsp;·&nbsp;
          <strong>Write</strong> = can create records &nbsp;·&nbsp;
          <strong>Update</strong> = can edit records.
          Changes take effect on the user's next navigation.
        </p>
        <p className="text-xs mt-1 italic" style={{ color:'var(--text-secondary)' }}>{EXEC_NOTE}</p>
      </div>

      {/* Role → Level mapping reference */}
      <div className="card overflow-hidden p-0">
        <div className="px-4 py-2.5 border-b text-xs font-semibold uppercase tracking-wider"
          style={{ borderColor:'var(--border)', background:'var(--surface-alt)', color:'var(--text-secondary)' }}>
          Permission Level → Roles
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x" style={{ borderColor:'var(--border)' }}>
          {Object.entries(LEVEL_ROLES).map(([level, roles]) => (
            <div key={level} className="px-3 py-2">
              <p className="text-xs font-bold mb-1" style={{ color:(PERM_LEVEL_COLOR_EXT||{})[level]||PERM_LEVEL_COLOR[level]||'#64748b' }}>{level}</p>
              {roles.map(r => <p key={r} className="text-xs" style={{ color:'var(--text-secondary)' }}>• {r}</p>)}
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 p-3 rounded-xl"
        style={{ background:'var(--surface-alt)', border:'1px solid var(--border)' }}>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold uppercase tracking-wider flex-shrink-0" style={{ color:'var(--text-secondary)' }}>Module</label>
          <select className="select text-sm" value={moduleFilter} onChange={e => setModuleFilter(e.target.value)}>
            <option value="All">All Modules</option>
            {Object.keys(pagesByGroup).map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold uppercase tracking-wider flex-shrink-0" style={{ color:'var(--text-secondary)' }}>Permission Level</label>
          <select className="select text-sm" value={levelFilter} onChange={e => setLevelFilter(e.target.value)}>
            {PERM_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold uppercase tracking-wider flex-shrink-0" style={{ color:'var(--text-secondary)' }}>Page</label>
          <select className="select text-sm w-56" value={pageFilter} onChange={e => setPageFilter(e.target.value)}>
            <option value="">All Pages</option>
            {[...pages].sort((a,b)=>(a.label||'').localeCompare(b.label||'')).map(p => (
              <option key={p.id} value={p.id}>{p.label} — {p.path}</option>
            ))}
          </select>
        </div>
        <span className="text-xs" style={{ color:'var(--text-secondary)' }}>
          Editing <strong style={{ color:PERM_LEVEL_COLOR[levelFilter] }}>{levelFilter}</strong> permissions
          {LOCKED_LEVELS.includes(levelFilter) && <span className="text-amber-600 font-medium ml-1">— {levelFilter} has full access by default</span>}
        </span>
      </div>

      {/* Permissions table */}
      <div className="card overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background:'var(--surface-alt)', borderBottom:'2px solid var(--border)' }}>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color:'var(--text-secondary)', minWidth:100 }}>Module</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color:'var(--text-secondary)', minWidth:160 }}>Page</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color:'var(--text-secondary)', minWidth:120 }}>Path</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-24" style={{ color:PERM_LEVEL_COLOR[levelFilter] }}>Access</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-24" style={{ color:'#10b981' }}>Read</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-24" style={{ color:'#f97316' }}>Write</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-24" style={{ color:'#8b5cf6' }}>Update</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(pagesByGroup)
                .filter(([grp]) => moduleFilter === 'All' || grp === moduleFilter)
                .map(([grp, groupPages]) => {
                  const filteredGrpPages = pageFilter
                    ? groupPages.filter(p => p.id === pageFilter)
                    : groupPages;
                  if (filteredGrpPages.length === 0) return null;
                  const allOn = filteredGrpPages.every(p => {
                    const r = p.access?.[levelFilter];
                    return typeof r === 'object' ? (r?.on ?? false) : (r ?? false);
                  });
                  return (
                    <React.Fragment key={grp}>
                      {/* Module header */}
                      <tr style={{ background:'rgba(46,83,159,.06)', borderBottom:'1px solid var(--border)', borderTop:'2px solid var(--border)' }}>
                        <td colSpan={7} className="px-4 py-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold uppercase tracking-widest" style={{ color:'#2E539F' }}>
                              {grp} <span className="font-normal normal-case ml-1" style={{ color:'var(--text-secondary)' }}>({groupPages.length} page{groupPages.length!==1?'s':''})</span>
                            </span>
                            {levelFilter !== 'Admin' && (
                              <button
                                onClick={async () => {
                                  for (const p of filteredGrpPages.filter(pg => !pg.always_visible)) {
                                    const r = p.access?.[levelFilter];
                                    const cur = typeof r === 'object' ? (r?.on ?? false) : (r ?? false);
                                    if (cur !== !allOn) await togglePageAccess(p.id, levelFilter, cur, 'access');
                                  }
                                }}
                                className="text-xs px-3 py-1 rounded-lg font-medium transition-colors"
                                style={{ background:allOn?'rgba(16,185,129,.12)':'rgba(239,68,68,.1)', color:allOn?'#059669':'#ef4444' }}>
                                {allOn ? '✓ All On — disable all' : '✗ All Off — enable all'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {filteredGrpPages.map((page, pi) => {
                        const raw  = page.access?.[levelFilter];
                        const isOn = typeof raw === 'object' ? (raw?.on ?? false) : (raw ?? false);
                        const rd   = typeof raw === 'object' ? (raw?.read   ?? true)  : isOn;
                        const wr   = typeof raw === 'object' ? (raw?.write  ?? false) : false;
                        const up   = typeof raw === 'object' ? (raw?.update ?? false) : false;
                        const locked = LOCKED_LEVELS.includes(levelFilter) || page.always_visible;

                        const Btn = ({ t, val, color }) => {
                          const k = page.id + levelFilter + t;
                          if (saving[k]) return (
                            <span className="w-16 h-7 flex items-center justify-center">
                              <span className="w-3.5 h-3.5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor:color }}/>
                            </span>
                          );
                          return (
                            <button disabled={locked} onClick={() => togglePageAccess(page.id, levelFilter, val, t)}
                              title={locked ? 'Cannot restrict this permission' : `${t}: ${val?'ON':'OFF'} — click to ${val?'disable':'enable'}`}
                              className="w-16 h-7 rounded-lg text-xs font-bold transition-all"
                              style={{
                                background: val ? `${color}18` : 'var(--surface-alt)',
                                color:      val ? color : 'var(--text-secondary)',
                                border:     `1.5px solid ${val?`${color}50`:'var(--border)'}`,
                                cursor:     locked ? 'default' : 'pointer',
                                opacity:    locked ? 0.6 : 1,
                              }}>
                              {val ? 'ON' : 'OFF'}
                            </button>
                          );
                        };

                        return (
                          <tr key={page.id}
                            className="border-b transition-colors hover:bg-[var(--surface-alt)]"
                            style={{ borderColor:'var(--border)', background:pi%2===1?'rgba(0,0,0,.015)':'transparent' }}>
                            <td className="px-4 py-2.5 text-xs font-medium" style={{ color:'#2E539F' }}>{grp}</td>
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm" style={{ color:'var(--text)' }}>{page.label}</span>
                                {page.always_visible && (
                                  <span className="text-xs px-1.5 py-0.5 rounded flex items-center gap-1"
                                    style={{ background:'var(--surface-alt)', color:'var(--text-secondary)' }}>
                                    <LockIc/> Core
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-2.5"><code className="text-xs" style={{ color:'var(--text-secondary)' }}>{page.path}</code></td>
                            <td className="px-4 py-2.5 text-center"><Btn t="access" val={isOn} color={PERM_LEVEL_COLOR[levelFilter]}/></td>
                            <td className="px-4 py-2.5 text-center"><Btn t="read"   val={rd}   color="#10b981"/></td>
                            <td className="px-4 py-2.5 text-center"><Btn t="write"  val={wr}   color="#f97316"/></td>
                            <td className="px-4 py-2.5 text-center"><Btn t="update" val={up}   color="#8b5cf6"/></td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t text-xs" style={{ borderColor:'var(--border)', color:'var(--text-secondary)' }}>
          Core pages (🔒) are always visible to all roles and cannot be restricted.
        </div>
      </div>
    </div>
  );
}
